import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../supabaseClient";
import ShieldImg from "../../Assets/Icons/shield.png";

const AdminVeteraniTeams = ({ tournamentId, turnir }) => {
  // Debugging: da vidimo u konzoli što točno stiže u komponentu
  console.log("AdminVeteraniTeams - primljeni propse:", {
    tournamentId,
    turnir,
  });

  const currentTourId = tournamentId || turnir?.id;
  console.log("AdminVeteraniTeams - izračunati currentTourId:", currentTourId);

  const availableGroupCounts = [2, 3, 4, 6, 8];
  const allPossibleGroups = ["A", "B", "C", "D", "E", "F", "G", "H"];

  const [numberOfGroups, setNumberOfGroups] = useState(2);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [teams, setTeams] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  // State za kreiranje novog tima
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamLogo, setNewTeamLogo] = useState(null);

  // State za uređivanje tima i igrača
  const [editingTeam, setEditingTeam] = useState(null);
  const [editTeamName, setEditTeamName] = useState("");
  const [editTeamLogo, setEditTeamLogo] = useState(null);
  const [playerInput, setPlayerInput] = useState({ no: "", name: "" });
  const [editingPlayerIndex, setEditingPlayerIndex] = useState(null);

  const fetchTeams = useCallback(async () => {
    if (!currentTourId) {
      console.log("fetchTeams preskočen: currentTourId ne postoji.");
      return;
    }

    console.log("Dohvaćam timove za turnir ID:", currentTourId);

    // Dohvat timova iz veteranske tablice
    const { data: teamsData, error: teamsError } = await supabase
      .from("turniri_veterani_timovi")
      .select("*")
      .eq("turnir_id", currentTourId);

    // Dohvat odigranih utakmica iz veteranske tablice utakmica
    const { data: matchesData, error: matchesError } = await supabase
      .from("turniri_veterani")
      .select("*")
      .eq("tournament_id", currentTourId)
      .eq("is_completed", true);

    if (teamsError || matchesError) {
      console.error("Greška kod dohvata:", teamsError || matchesError);
      return;
    }

    const stats = {};
    (teamsData || []).forEach((t) => {
      stats[t.id] = {
        ...t,
        played: 0,
        points: 0,
        goals_for: 0,
        goals_against: 0,
      };
    });

    (matchesData || []).forEach((m) => {
      const tA = stats[m.team_a_id];
      const tB = stats[m.team_b_id];
      if (tA && tB) {
        tA.played += 1;
        tB.played += 1;
        tA.goals_for += m.scorea;
        tA.goals_against += m.scoreb;
        tB.goals_for += m.scoreb;
        tB.goals_against += m.scorea;
        if (m.scorea > m.scoreb) tA.points += 3;
        else if (m.scorea < m.scoreb) tB.points += 3;
        else {
          tA.points += 1;
          tB.points += 1;
        }
      }
    });

    const processedTeams = Object.values(stats).sort(
      (a, b) =>
        b.points - a.points ||
        b.goals_for - b.goals_against - (a.goals_for - a.goals_against) ||
        b.goals_for - a.goals_for,
    );

    setTeams(processedTeams);
  }, [currentTourId]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  // Dohvat spremljenog broja grupa iz baze pri učitavanju
  useEffect(() => {
    if (!currentTourId) return;

    supabase
      .from("admin_turniri")
      .select("veterani_broj_grupa")
      .eq("id", currentTourId)
      .single()
      .then(({ data, error }) => {
        if (data && data.veterani_broj_grupa !== null) {
          setNumberOfGroups(data.veterani_broj_grupa);
        } else if (error) {
          console.error("Greška kod dohvata broja grupa za veterane:", error);
        }
      });
  }, [currentTourId]);

  useEffect(() => {
    if (editingTeam) {
      const current = teams.find((t) => t.id === editingTeam.id);
      if (current && JSON.stringify(current) !== JSON.stringify(editingTeam)) {
        setEditingTeam(current);
      }
    }
  }, [teams, editingTeam]);

  // Funkcija za spremanje odabranog broja grupa u bazu
  const handleGroupCountChange = async (newCount) => {
    setNumberOfGroups(newCount);
    if (!currentTourId) return;

    const { error } = await supabase
      .from("admin_turniri")
      .update({ veterani_broj_grupa: newCount })
      .eq("id", currentTourId);

    if (error) {
      console.error("Greška kod spremanja broja grupa:", error);
      alert("Greška kod spremanja broja grupa na server.");
    }
  };

  const compressImage = (file, maxWidth = 300, maxHeight = 300) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], `${Date.now()}.webp`, {
                  type: "image/webp",
                });
                resolve(compressedFile);
              } else {
                reject(new Error("Kompresija nije uspjela."));
              }
            },
            "image/webp",
            0.8,
          );
        };
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const uploadLogo = async (file) => {
    const compressedFile = await compressImage(file);
    const fileName = `veterani-${Math.random()}-${Date.now()}.webp`;
    const filePath = `team-logos/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from("logos")
      .upload(filePath, compressedFile);

    if (uploadError) throw uploadError;
    const { data } = supabase.storage.from("logos").getPublicUrl(filePath);
    return data.publicUrl;
  };

  const handleSaveNewTeam = async () => {
    console.log("Pokušaj spremanja tima. currentTourId je:", currentTourId);

    if (!currentTourId) {
      alert(
        "Greška: ID turnira još nije učitan. Molimo pričekajte sekundu pa pokušajte ponovno.",
      );
      return;
    }
    if (!newTeamName.trim()) return alert("Upišite ime tima!");
    if (!selectedGroup) return alert("Greška: Grupa nije odabrana!");

    setIsUploading(true);
    try {
      let finalLogoUrl = null;
      if (newTeamLogo) {
        finalLogoUrl = await uploadLogo(newTeamLogo);
      }

      const { error } = await supabase.from("turniri_veterani_timovi").insert([
        {
          turnir_id: currentTourId,
          name: newTeamName.trim(),
          group_name: selectedGroup,
          logo_url: finalLogoUrl,
          players: [],
        },
      ]);

      if (error) throw error;

      setIsTeamModalOpen(false);
      setNewTeamName("");
      setNewTeamLogo(null);
      await fetchTeams();
    } catch (err) {
      console.error("Greška kod spremanja tima:", err);
      alert("Greška kod spremanja tima: " + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const openEditModal = (team) => {
    setEditingTeam(team);
    setEditTeamName(team.name);
    setEditTeamLogo(team.logo_url);
    setPlayerInput({ no: "", name: "" });
    setEditingPlayerIndex(null);
    setIsEditModalOpen(true);
  };

  const handleUpdateTeamData = async () => {
    if (!editTeamName) return alert("Ime tima ne može biti prazno!");
    setIsUploading(true);
    try {
      let finalLogoUrl = editingTeam.logo_url;
      if (editTeamLogo && typeof editTeamLogo !== "string") {
        finalLogoUrl = await uploadLogo(editTeamLogo);
      }

      const { error } = await supabase
        .from("turniri_veterani_timovi")
        .update({ name: editTeamName, logo_url: finalLogoUrl })
        .eq("id", editingTeam.id);

      if (error) throw error;
      alert("Uspješno spremljeno!");
      fetchTeams();
    } catch (err) {
      alert(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const formatPlayerName = (fullName) => {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 0) return "";
    return parts
      .map((part, index) => {
        if (index === parts.length - 1) {
          return part;
        }

        return `${part.charAt(0).toUpperCase()}.`;
      })
      .join(" ");
  };

  const handleSavePlayer = async () => {
    if (!playerInput.name || !playerInput.no)
      return alert("Popunite polja za igrača!");

    const formattedName = formatPlayerName(playerInput.name);

    let updatedPlayers = [...(editingTeam.players || [])];

    if (editingPlayerIndex !== null) {
      updatedPlayers[editingPlayerIndex] = {
        ...updatedPlayers[editingPlayerIndex],
        name: formattedName,
        no: parseInt(playerInput.no),
      };
    } else {
      updatedPlayers.push({
        name: formattedName,
        no: parseInt(playerInput.no),
        goals: 0,
      });
    }

    const { error } = await supabase
      .from("turniri_veterani_timovi")
      .update({ players: updatedPlayers })
      .eq("id", editingTeam.id);

    if (!error) {
      setPlayerInput({ no: "", name: "" });
      setEditingPlayerIndex(null);
      fetchTeams();
    } else {
      alert("Greška: " + error.message);
    }
  };

  const handleDeletePlayer = async (index) => {
    if (!window.confirm("Obrisati igrača?")) return;
    let updatedPlayers = [...(editingTeam.players || [])];
    updatedPlayers.splice(index, 1);

    const { error } = await supabase
      .from("turniri_veterani_timovi")
      .update({ players: updatedPlayers })
      .eq("id", editingTeam.id);

    if (!error) {
      fetchTeams();
    } else {
      alert("Greška: " + error.message);
    }
  };

  const handleDeleteTeam = async (id) => {
    if (window.confirm("Obrisati cijeli tim i sve igrače?")) {
      await supabase.from("turniri_veterani_timovi").delete().eq("id", id);
      setIsEditModalOpen(false);
      setEditingTeam(null);
      fetchTeams();
    }
  };

  const activeGroups = allPossibleGroups.slice(0, numberOfGroups);
  const groupTeams = teams.filter((t) => t.group_name === selectedGroup);

  return (
    <div className="admin-teams-container admin-veterani-teams">
      {!selectedGroup && (
        <div className="group-count-selector-bar">
          <label>Broj grupa:</label>
          <select
            value={numberOfGroups}
            onChange={(e) => handleGroupCountChange(parseInt(e.target.value))}
          >
            {availableGroupCounts.map((num) => (
              <option key={num} value={num}>
                {num} {num === 2 ? "grupe" : num === 3 ? "grupe" : ` grupa`}
              </option>
            ))}
          </select>
        </div>
      )}

      {!selectedGroup ? (
        <div className="groups-grid">
          {activeGroups.map((g) => {
            const count = teams.filter((t) => t.group_name === g).length;
            return (
              <div
                key={g}
                className="group-card"
                onClick={() => setSelectedGroup(g)}
              >
                <div className="group-card-badge">GRUPA</div>
                <h3>{g}</h3>
                <div className="group-card-badge-float">{g}</div>
                <span className="team-count">
                  <i className="fi fi-rs-users-alt"></i> Broj ekipa:{" "}
                  <strong>{count}</strong>
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="group-detail">
          <button
            className="back-btn-simple"
            onClick={() => setSelectedGroup(null)}
          >
            <i className="fi fi-rs-angle-left"></i> Popis grupa (Veterani)
          </button>

          <div className="header-row">
            <h3>VETERANI - GRUPA {selectedGroup}</h3>
            <button
              className="add-btn-main"
              onClick={() => setIsTeamModalOpen(true)}
            >
              <i className="fi fi-rs-plus-small"></i> DODAJ VETERANSKI TIM
            </button>
          </div>

          {groupTeams.length === 0 ? (
            <div className="no-teams-placeholder">
              <i className="fi fi-rs-shield-check"></i>
              <p>Još nema dodanih veteranskih timova u ovoj grupi.</p>
            </div>
          ) : (
            <div className="table-responsive-wrapper">
              <table className="teams-table-preview">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Ekipa (Veterani)</th>
                    <th className="text-center">UT</th>
                    <th className="text-center">GR</th>
                    <th className="text-center">BOD</th>
                    <th className="text-right">Akcija</th>
                  </tr>
                </thead>
                <tbody>
                  {groupTeams.map((team, index) => {
                    const goalDiff = team.goals_for - team.goals_against;
                    return (
                      <tr key={team.id}>
                        <td className="team-position">{index + 1}.</td>
                        <td>
                          <div
                            className="team-table-identity"
                            onClick={() => openEditModal(team)}
                          >
                            <img src={team.logo_url || ShieldImg} alt="logo" />
                            <span className="team-name-txt">{team.name}</span>
                          </div>
                        </td>
                        <td className="text-center text-muted-table">
                          {team.played}
                        </td>
                        <td className="text-center text-muted-table">
                          {goalDiff > 0 ? `+${goalDiff}` : goalDiff}
                        </td>
                        <td className="text-center team-points-cell">
                          {team.points}
                        </td>
                        <td className="text-right">
                          <button
                            onClick={() => openEditModal(team)}
                            className="action-edit-btn"
                          >
                            <i className="fi fi-rs-settings"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* MODAL ZA DODAVANJE NOVOG TIMA */}
      {isTeamModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => setIsTeamModalOpen(false)}
        >
          <div
            className="event-modal animate-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-container">
              <h3>Novi Veteranski Tim (Grupa {selectedGroup})</h3>
              <i
                className="fi fi-rs-cross close-modal-icon"
                onClick={() => setIsTeamModalOpen(false)}
              ></i>
            </div>

            <div className="form-group">
              <label>Naziv Ekipe</label>
              <input
                type="text"
                placeholder="Unesi ime veteranske ekipe..."
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Grb / Logo</label>
              <label
                htmlFor="new-veteran-logo-upload"
                className="custom-file-upload"
              >
                {newTeamLogo ? (
                  <div className="logo-preview-badge">
                    <i
                      className="fi fi-rs-check-circle"
                      style={{ color: "#2ecc71" }}
                    ></i>
                    <span>Slika uspješno dodana</span>
                  </div>
                ) : (
                  <>
                    <i className="fi fi-rs-plus-small"></i> Dodaj sliku
                  </>
                )}
              </label>
              <input
                id="new-veteran-logo-upload"
                type="file"
                accept="image/*"
                onChange={(e) => setNewTeamLogo(e.target.files[0])}
                style={{ display: "none" }}
              />
            </div>
            <button
              className="add-btn"
              onClick={handleSaveNewTeam}
              disabled={isUploading}
            >
              {isUploading ? "Spremanje..." : "SPREMI VETERANSKI TIM"}
            </button>
          </div>
        </div>
      )}

      {/* MODAL ZA UREĐIVANJE TIMA */}
      {isEditModalOpen && editingTeam && (
        <div
          className="modal-overlay"
          onClick={() => {
            setIsEditModalOpen(false);
            setEditingTeam(null);
          }}
        >
          <div
            className="event-modal edit-team-modern-modal animate-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-container">
              <div className="modal-title-with-img">
                <img
                  src={editingTeam.logo_url || ShieldImg}
                  alt="logo-preview"
                />
                <h3>Uredi veterane: {editingTeam.name}</h3>
              </div>
              <i
                className="fi fi-rs-cross close-modal-icon"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingTeam(null);
                }}
              ></i>
            </div>

            <div className="modal-scrollable-content">
              <div className="stats-readonly-grid">
                <div className="s-item">
                  <span>Odigrano</span>
                  <strong>{editingTeam.played}</strong>
                </div>
                <div className="s-item">
                  <span>GR</span>
                  <strong>
                    {editingTeam.goals_for - editingTeam.goals_against}
                  </strong>
                </div>
                <div className="s-item accent">
                  <span>Bodovi</span>
                  <strong>{editingTeam.points}</strong>
                </div>
                <div className="s-item">
                  <span>Grupa</span>
                  <strong>{editingTeam.group_name}</strong>
                </div>
              </div>

              <div className="modern-section-box">
                <h4>
                  <i className="fi fi-rs-shield"></i> Informacije o klubu
                </h4>
                <div className="form-group-row">
                  <div className="form-group flex-2">
                    <label>Naziv ekipe</label>
                    <input
                      type="text"
                      value={editTeamName}
                      onChange={(e) => setEditTeamName(e.target.value)}
                    />
                  </div>
                  <div className="form-group flex-1">
                    <label>Grb kluba</label>
                    <label
                      htmlFor="edit-veteran-logo-upload"
                      className="custom-file-upload"
                    >
                      {editTeamLogo && typeof editTeamLogo === "string" ? (
                        <div className="logo-preview-badge">
                          <img src={editTeamLogo} alt="Preview" />
                          <span>Promijeni sliku</span>
                        </div>
                      ) : editTeamLogo && typeof editTeamLogo === "object" ? (
                        <div className="logo-preview-badge">
                          <span>Odabrana nova slika</span>
                        </div>
                      ) : (
                        <>
                          <i className="fi fi-rs-plus-small"></i> Dodaj sliku
                        </>
                      )}
                    </label>
                    <input
                      id="edit-veteran-logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={(e) => setEditTeamLogo(e.target.files[0])}
                      style={{ display: "none" }}
                    />
                  </div>
                </div>
                <button
                  className="inline-update-btn"
                  onClick={handleUpdateTeamData}
                  disabled={isUploading}
                >
                  {isUploading ? "Spremanje..." : "Spremi promjene"}
                </button>
              </div>

              <div className="modern-section-box">
                <h4>
                  <i className="fi fi-rs-users"></i> Sastav tima (
                  {editingTeam.players?.length || 0})
                </h4>

                <div className="player-fast-insert-form">
                  <input
                    type="number"
                    placeholder="Broj"
                    className="inp-no"
                    value={playerInput.no}
                    onChange={(e) =>
                      setPlayerInput({ ...playerInput, no: e.target.value })
                    }
                  />
                  <input
                    type="text"
                    placeholder="npr. M. Marić"
                    className="inp-name"
                    value={playerInput.name}
                    onChange={(e) =>
                      setPlayerInput({ ...playerInput, name: e.target.value })
                    }
                  />
                  <button
                    className="btn-player-action"
                    onClick={handleSavePlayer}
                  >
                    {editingPlayerIndex !== null ? "Spremi" : "Dodaj"}
                  </button>
                </div>

                <div className="players-list-modern-wrapper">
                  {!editingTeam.players || editingTeam.players.length === 0 ? (
                    <p className="empty-players-label">
                      Nema dodanih igrača u timu.
                    </p>
                  ) : (
                    editingTeam.players.map((p, idx) => (
                      <div
                        key={idx}
                        className={`player-modern-row ${editingPlayerIndex === idx ? "under-edit" : ""}`}
                      >
                        <div className="p-info">
                          <span className="p-number">#{p.no}</span>
                          <span className="p-name">{p.name}</span>
                        </div>
                        <div className="p-actions">
                          <button
                            className="act-btn e"
                            onClick={() => {
                              setEditingPlayerIndex(idx);
                              setPlayerInput({
                                no: p.no.toString(),
                                name: p.name,
                              });
                            }}
                          >
                            <i className="fi fi-rs-pencil"></i>
                          </button>
                          <button
                            className="act-btn d"
                            onClick={() => handleDeletePlayer(idx)}
                          >
                            <i className="fi fi-rs-trash"></i>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="danger-zone-modern">
                <button
                  className="btn-delete-team-destructive"
                  onClick={() => handleDeleteTeam(editingTeam.id)}
                >
                  <i className="fi fi-rs-trash"></i> Obriši ovaj tim i sve
                  podatke
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVeteraniTeams;
